import { AddDialog } from '@/components/AddDialog';
import { useProjectFilter } from '@/features/projects';
import { getFilterLabel } from '@/shared/copy/filters';
import { componentSizes } from '@/theme/componentSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r } from '@/theme/tokens/radii';
import { DialogBody, DialogSection, Text } from '@/ui';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@/ui/primitives';
import type { Dispatch, ReactElement, SetStateAction } from 'react';

type UploadFilesDialogProps = {
  open: boolean;
  title: string;
  saveLabel: string;
  saving: boolean;
  disableSave: boolean;
  errorMessage?: string | null;
  projectOptions: Array<{ id: string; name: string }>;
  uploadProjectId: string;
  setUploadProjectId: (value: string) => void;
  uploadFiles: File[];
  setUploadFiles: Dispatch<SetStateAction<File[]>>;
  selectedFiles: Set<string>;
  setSelectedFiles: Dispatch<SetStateAction<Set<string>>>;
  onClose: () => void;
  onSave: () => void;
};

export const UploadFilesDialog = ({
  open,
  title,
  saveLabel,
  saving,
  disableSave,
  errorMessage,
  projectOptions,
  uploadProjectId,
  setUploadProjectId,
  uploadFiles,
  setUploadFiles,
  selectedFiles,
  setSelectedFiles,
  onClose,
  onSave,
}: UploadFilesDialogProps): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const AddFilesButton = Button;
  const FileCheckbox = Checkbox;
  const listRadius = r('card');
  const listPadding = 0;
  return (
    <AddDialog
      open={open}
      title={title}
      saveLabel={saveLabel}
      saving={saving}
      disableSave={disableSave}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <DialogBody gap="md">
        {selectedProjectId ? (
          <DialogSection>
            <Text role="body" color="textSecondary">
              Uploading to current project context.
            </Text>
          </DialogSection>
        ) : (
          <DialogSection>
            <SingleSelectFilterField
              labelPlacement="stacked"
              required
              label={getFilterLabel('project')}
              value={uploadProjectId}
              fullWidth
              options={projectOptions.map((project) => ({
                value: project.id,
                label: project.name,
              }))}
              onChange={(nextValue) => {
                setUploadProjectId(nextValue);
              }}
            />
          </DialogSection>
        )}
        <DialogSection>
          <List
            dense
            sx={{
              border: (theme) => `1px solid ${resolveSemanticColor(theme, 'border')}`,
              borderRadius: listRadius,
              p: listPadding,
              maxHeight: componentSizes.uploads.listMaxHeight,
              overflow: 'auto',
            }}
          >
            {uploadFiles.length === 0 ? (
              <ListItem>
                <ListItemText primary="No files selected" secondary="Add files to upload" />
              </ListItem>
            ) : (
              uploadFiles.map((file) => (
                <ListItem
                  key={file.name}
                  secondaryAction={
                    <FileCheckbox
                      edge="end"
                      checked={selectedFiles.has(file.name)}
                      onChange={(event) => {
                        setSelectedFiles((current) => {
                          const next = new Set(current);
                          if (event.target.checked) {
                            next.add(file.name);
                          } else {
                            next.delete(file.name);
                          }
                          return next;
                        });
                      }}
                    />
                  }
                >
                  <ListItemButton
                    onClick={() => {
                      setSelectedFiles((current) => {
                        const next = new Set(current);
                        if (next.has(file.name)) {
                          next.delete(file.name);
                        } else {
                          next.add(file.name);
                        }
                        return next;
                      });
                    }}
                  >
                    <ListItemIcon>
                      <FileCheckbox edge="start" checked={selectedFiles.has(file.name)} />
                    </ListItemIcon>
                    <ListItemText primary={file.name} secondary={file.type || 'Unknown type'} />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </DialogSection>
        <DialogSection>
          <AddFilesButton
            variant="secondary"
            component="label"
            fullWidthOnMobile={false}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Files
            <input
              type="file"
              hidden
              multiple
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                if (files.length === 0) return;
                setUploadFiles((current) => {
                  const byName = new Map(current.map((file) => [file.name, file]));
                  files.forEach((file) => {
                    byName.set(file.name, file);
                  });
                  return Array.from(byName.values());
                });
                setSelectedFiles((current) => {
                  const next = new Set(current);
                  files.forEach((file) => next.add(file.name));
                  return next;
                });
                event.target.value = '';
              }}
            />
          </AddFilesButton>
        </DialogSection>
      </DialogBody>
    </AddDialog>
  );
};
